/* eslint-disable @typescript-eslint/no-use-before-define */
import _ from 'lodash';

import constants from '../../settings/constants';
import geometry from '../../scene/util/geometry';
import productService from '../../services/products';
import { floor } from '../../util/round';
import { filter } from '../../util/propFilter';
import {
  getOpenMatchingConnections,
  fitToPoint,
  getBounds,
  expandBounds,
  isWallMounted,
  isFloorStanding,
  getMatchingConnections,
  connectionPointOpen,
  topPropBounds,
} from '../../services/products/models';
import { flatten, unique } from '../../util/array';
import idGenerator from '../../util/aactools/idGenerator';
import { range } from './range';
import { config as asConfig } from '../../scene/boaxel/AdjustableConfig';
import convert from '../../util/aactools/convert';
import { isFixedRoom } from '../../util/room';
import getItemConfig from '../../scene/util/getItemConfig';
import { ServiceSettings } from '@ikea-aoa/ikea-shared-services';
import { ITEMS } from '../../constants';
import { getArticleContent } from '../../services/products/articles';

const cache = {
  state: {
    tac: null,
    tacItems: null,
    getAllItems: null,
  },
  temp: {
    tacItems: null,
    getAllItems: null,
  },
};

function clearCache(tac) {
  Object.keys(cache.state).forEach(key => {
    cache.state[key] = null;
  });
  cache.state.tac = tac;
  cache.state.tacItems = getItems(tac);
}

function getItem(tac, itemid) {
  return getAllItems(getItems(tac)).find(item => item.itemid === itemid);
}

function getItems(tac) {
  return tac.items;
}

export function getItemPrice(item) {
  const articleContent = getArticleContent(item.id);
  return articleContent.priceInformation.salesPrice[0].priceInclTax;
}

export function getAllItems(items = [], omit) {
  if (cache.state.tacItems === items) {
    // this is the correct tac to cache
    if (cache.state.getAllItems) {
      return removeTree(cache.state.getAllItems, omit);
    }
    // this was first call using this tac
    cache.state.getAllItems = digAllItems(items);
    return removeTree(cache.state.getAllItems, omit);
  }
  // this is not the state tac. cache it somewhere else in case we're doing getOpenSlots
  if (cache.temp.tacItems !== items) {
    cache.temp.getAllItems = digAllItems(items);
  }
  return removeTree(cache.temp.getAllItems, omit);
}

function removeTree(items, remove = {}) {
  const root = items.find(item => item.itemid === remove.itemid) || {
    items: [],
  };
  const removeIDs = digAllItems(root.items || [])
    .map(item => item.itemid)
    .concat(root.itemid)
    .filter(Boolean);

  return items.filter(item => removeIDs.indexOf(item.itemid) === -1);
}

export function digAllItems(items) {
  return items
    .map(item => {
      return digAllItems(item.items || []).concat(item);
    })
    .reduce((acc, item) => acc.concat(item), []);
}

function getItemIds(parent) {
  return parent.items.reduce((ids, item) => {
    ids.push(item.itemid);

    if (item.items) {
      return ids.concat(getItemIds(item));
    } else {
      return ids;
    }
  }, []);
}

/**
 * Recursively checks whether an item has any real articles
 * @param {Object} item
 * @returns {Boolean}
 */
const hasRealArticle = item => {
  return (
    productService.isRealArticleProduct(item) ||
    item.items?.some(hasRealArticle)
  );
};

/**
 * Recursively maps an item to only the properties wanted when comparing for changes
 * @param {Object} item
 * @param {Array} props the props to include in the return object
 * @param {Boolean|undefined} realArticles Whether to include only items with real articles
 * @returns {Object}
 */
const getComparableItem = (item, props, realArticles) => {
  if (realArticles && !hasRealArticle(item)) {
    return;
  }

  return props.reduce((acc, prop) => {
    return {
      ...acc,
      [prop]:
        prop === 'items'
          ? _.sortBy(
              item.items
                ?.map(item => getComparableItem(item, props))
                .filter(Boolean) || [],
              props
            )
          : item[prop],
    };
  }, {});
};
/**
 * Checks whether to version of tac items are identical in all important aspects
 *
 * @param {Array} currentItems
 * @param {Array} previousItems
 * @param {Object} [options] Specific options for the comparison
 * @returns {Boolean}
 */
const itemsUnchanged = (currentItems, previousItems, options = {}) => {
  const { props: specificProps, realArticles } = options;
  const props = specificProps || [
    'id',
    'x',
    'y',
    'z',
    'width',
    'height',
    'depth',
    'items',
  ];

  const currentForCompare = currentItems
    .map(item => getComparableItem(item, props, realArticles))
    .filter(Boolean);
  const previousForCompare = previousItems
    .map(item => getComparableItem(item, props, realArticles))
    .filter(Boolean);

  return _.isEqual(
    _.sortBy(currentForCompare, props),
    _.sortBy(previousForCompare, props)
  );
};

function isUnchanged(curr, prev) {
  if (curr === prev) {
    return true;
  }

  if (curr.items === prev.items && curr.wall === prev.wall) {
    return true;
  }

  return (
    _.isEqual(curr.wall, prev.wall) &&
    itemsUnchanged(curr.items, prev.items) &&
    _.isEqualWith(curr.settings, prev.settings, (value1, value2, key) => {
      return key === 'filter' ? true : undefined;
    })
  );
}

function getSections(tac, ...filters) {
  let sections = tac.items.filter(productService.isSection);

  if (filters) {
    sections = productService.filter(sections, ...filters);
  }

  return sections;
}

function getClothesRails(tac, ...filters) {
  let clothesRails = getAllItems(tac.items).filter(item =>
    productService.isType(item, ITEMS.CLOTHES_RAIL)
  );

  if (filters) {
    clothesRails = productService.filter(clothesRails, ...filters);
  }

  return clothesRails.map(cr => {
    const out = Object.assign({}, cr);
    out.parentItemId = getParent(tac, cr).itemid;
    return out;
  });
}

function getTopAncestor(tac, item) {
  let ancestor;
  let parent = item; // start value assuming item itself is stand alone
  while (parent && parent !== tac) {
    ancestor = parent;
    parent = getParent(tac, parent);
  }
  return ancestor;
}

function getPotentialBlockers(tac, item) {
  const allRelativesWithGlobalPos = getRawRects(tac, item);

  return allRelativesWithGlobalPos
    .filter(
      relative =>
        relative.logic &&
        relative.logic.extendable &&
        relative.connectsTo &&
        geometry.eclipsed(item, relative) &&
        getTopAncestor(tac, relative.connectsTo).itemid !==
          getTopAncestor(tac, item).itemid
    )
    .map(potentialBlocker => {
      return {
        ...potentialBlocker,
        topAncestor: getTopAncestor(tac, potentialBlocker),
      };
    });
}

function getParent(tac, item) {
  const allItems = getAllItems(getItems(tac));
  const parent = allItems.find(
    candidate =>
      candidate.items &&
      candidate.items.find(child => child.itemid === item.itemid)
  );

  if (parent) {
    return parent;
  }
  if (allItems.find(candidate => candidate.itemid === item.itemid)) {
    return tac;
  }
}

function getGlobalCoords(item, tac) {
  if (!item || item === tac) {
    return { x: 0, y: 0, z: 0 };
  }
  const parent = getParent(tac, item);
  const parentCoords = getGlobalCoords(parent, tac);

  return {
    ...item,
    x: item.x + parentCoords.x,
    y: item.y + parentCoords.y,
    z: item.z + parentCoords.z,
  };
}

function getArea(item, parent, connection, tac) {
  const placed = fitToPoint(item, connection, null, true);

  //only offset coords for child items
  let parentOffset = tac.items.includes(parent) ? { x: 0, y: 0, z: 0 } : parent;
  parentOffset = getGlobalCoords(parent, tac);

  return {
    x: placed.x + parentOffset.x,
    y: placed.y + parentOffset.y,
    z: placed.z + parentOffset.z,
    depth: placed.depth,
    width: placed.width,
    local: placed,
  };
}

function filterOutParts(parentItem) {
  return parentItem.items && parentItem.items.length && parentItem.parts
    ? parentItem.items.filter(
        childItem =>
          !Object.values(parentItem.parts).some(part => part === childItem.id)
      )
    : parentItem.items;
}

function getRoomSlots(space, rects, fitItem, slotItem, tac) {
  const fullFit = getFullSize(fitItem);
  const fullSlot = getFullSize(slotItem);

  return geometry
    .sliceRectangle(space, rects)
    .map(rect => ({ ...rect, z: 0, depth: constants.ROOM_DEPTH }))
    .filter(slot => {
      if (geometry.fits(slot, fullFit)) {
        const allowedOverlap = range.getAllowedOverlap(tac, slotItem, slotItem);
        if (allowedOverlap) {
          // if the slot is wider than our item,
          // but not wide enough to fit an entire overlap,
          // we can't fit
          return (
            slot.width === fullFit.width ||
            slot.width >= fullFit.width + allowedOverlap.x ||
            geometry.getCollidingRects(slot, rects, {
              left: allowedOverlap.x,
              right: allowedOverlap.x,
            }).length < 2
          );
        }
        return true;
      }
      //fullsize didnt fit, but if naked does, maybe we can squeeze it in
      if (geometry.fits(slot, fullSlot)) {
        // first find what prevented fullSize fit
        const collidingParts = rects.filter(rect =>
          geometry.collides(rect, fullFit)
        );
        // use individual parts instead of the fullSize
        const itemParts = getRects({ items: [slotItem] });
        // and see if the parts can coexist
        const collides = collidingParts.some(coll =>
          itemParts.some(rect => geometry.collides(rect, coll))
        );
        return !collides;
      }
      return false;
    });
}

function getVariants(currentItem, options = {}) {
  const swappableFilters = {
    color: currentItem.filter.color,
    variant: currentItem.filter.variant,
  };
  if (options.extendabilityLocked) {
    swappableFilters.extendable = currentItem.logic.extendable;
  }
  // to fit an item that comes in many sizes, eg a shelf, we find all similar items
  return productService
    .getSwappables(currentItem, swappableFilters)
    .map(variant => ({
      ...variant,
      itemid: currentItem.itemid, // and treat them as the same item
      items: filterOutParts(currentItem), // keep children, except those listed as parts on the item
    }));
}

function getSlot(outer, inner, tac, currentItem) {
  // since variants may be of different size
  // we need to figure out the size of the slot it goes into
  const box = getArea(outer.variant, outer, inner, tac);
  return {
    // make sure that it's the same object as in the tac,
    // since filtering in Section.drawDropAreas depends on this
    parent: {
      ...getItem(tac, outer.itemid),
    },
    ...currentItem,
    ...outer.variant,
    x: box.x,
    y: box.y,
    z: box.z,
    local: box.local,
    depth: box.depth,
    width: box.width,
  };
}

function hasOpenSlot(tac, currentItem, rebasedItems) {
  const variantOptions = {
    extendabilityLocked: currentItem.logic?.extendabilityLockedOnDrag,
  };

  const variants = range.filterVariants(
    tac,
    range.preScreenSlots(getVariants(currentItem, variantOptions), tac)
  );

  return variants.some(variant => {
    const connectionGroups = getMatchingConnections(variant, tac);

    let partnerSlots;

    if (productService.isMultiParentProduct(variant)) {
      partnerSlots = getOpenPartnerSlots(tac, variant, {
        noVariants: true,
      });
    }
    return connectionGroups.some(parent => {
      return parent.connections.some(point => {
        if (!connectionPointOpen(point, variant, parent, rebasedItems, tac)) {
          return false;
        }

        let slots = [getSlot(parent, point, tac, currentItem)];

        if (partnerSlots) {
          slots = range.partnerSlots(slots, partnerSlots, tac);
        }
        return range.filterSlots(slots, tac).length;
      });
    });
  });
}

function getOpenSlots(tac, currentItem, options) {
  return getSlots(tac, currentItem, options, getOpenMatchingConnections);
}

function getSlots(
  tac,
  currentItem,
  options = {},
  connectionFinder = getMatchingConnections
) {
  const { noVariants, extendabilityLocked, proposedParent } = options;
  const allVariants = noVariants
    ? [currentItem]
    : getVariants(currentItem, { extendabilityLocked: extendabilityLocked });
  const variants = range.filterVariants(tac, allVariants);

  // now get connections for any size of currentItem
  const allItems = getAllItems(tac.items);

  // omit any clothes rails that connect to the item being fitted so it ca be put back
  const attachedExtendables = allItems.filter(
    item =>
      item.logic &&
      item.logic.extendable &&
      item.connectsTo &&
      item.connectsTo.itemid === currentItem.itemid
  );

  const dependentItems = range.getDependentItems(currentItem, tac);

  const outsiders = allItems.filter(item => {
    if (!proposedParent) {
      return !isWithinWall(item, tac);
    }
    return (
      proposedParent.itemid !== item.itemid &&
      !isAncestor(tac, proposedParent, item) &&
      !isWithinWall(item, tac)
    );
  });

  const ignored = [
    ...attachedExtendables,
    ...outsiders,
    ...dependentItems,
    currentItem,
  ].map(item => item.itemid);

  const examinedTac = filterTac(tac, ignored);

  const connections = flatten(
    variants.map(variant =>
      connectionFinder(variant, examinedTac, proposedParent)
    )
  ).map(outer => {
    return outer.connections.map(inner =>
      getSlot(outer, inner, tac, currentItem)
    );
  });

  let allSlots = flatten(connections).filter(unique);
  if (
    !options.isPartnerCall &&
    variants.some(variant => productService.isMultiParentProduct(variant))
  ) {
    const partnerSlots = getOpenPartnerSlots(tac, currentItem, options);
    allSlots = range.partnerSlots(allSlots, partnerSlots, tac);
  }

  return range.filterSlots(allSlots, tac);
}

function getMeasureBounds(item, options) {
  const bounds = {
    x: item.x || 0,
    y: item.y || 0,
    z: item.z || 0,
    height: item.height || 0,
    width: item.width || 0,
    depth: item.depth || 0,
  };

  if (productService.isType(item, ITEMS.SHOE_SHELF)) {
    const proppingOffset = topPropBounds(item);
    bounds.y += proppingOffset.height;
  }

  if (
    options &&
    options.realArticles &&
    (!item.id || !productService.isRealArticleProduct(item.id))
  ) {
    bounds.height = 0;
    bounds.width = 0;
    bounds.depth = 0;
  }
  return bounds;
}

function getLimits(item, room, options) {
  if (!item.items || item.items.length === 0) {
    const bounds = getMeasureBounds(item, options);
    return {
      min: {
        x: bounds.x,
        y: room ? 0 : bounds.y,
        z: bounds.z,
      },
      max: {
        x: bounds.x + bounds.width,
        y: bounds.y + bounds.height,
        z: bounds.z + bounds.depth,
      },
    };
  }

  const out = item.items.reduce(
    (limits, child) => {
      if (Array.isArray(child.items) && child.items.length) {
        const childLimits = getLimits(child, room, options);
        const x = Math.min(child.x, child.x + childLimits.min.x);
        const y = Math.min(child.y, child.y + childLimits.min.y);
        const z = Math.min(child.z, child.z + childLimits.min.z);
        if (
          options?.realArticles &&
          !productService.isRealArticleProduct(child.id)
        ) {
          child = { ...child, height: 0, width: 0, depth: 0 };
        }
        const width =
          Math.max(child.x + childLimits.max.x, child.x + child.width) - x;
        const height =
          Math.max(child.y + childLimits.max.y, child.y + child.height) - y;
        const depth =
          Math.max(child.z + childLimits.max.z, child.z + child.depth) - z;

        child = {
          id: child.id,
          x,
          y,
          z,
          width,
          height,
          depth,
          condensed: true,
        };
      }

      if (!productService.isRealArticleProduct(child) && !child.condensed) {
        return limits;
      }
      const bounds = getMeasureBounds(child);

      limits.min.x = Math.min(limits.min.x, bounds.x);
      limits.min.y = Math.min(limits.min.y, bounds.y);
      limits.min.z = Math.min(limits.min.z, bounds.z);
      limits.max.x = Math.max(limits.max.x, bounds.x + bounds.width);
      limits.max.y = Math.max(limits.max.y, bounds.y + bounds.height);
      limits.max.z = Math.max(limits.max.z, bounds.z + bounds.depth);

      return limits;
    },
    {
      min: {
        x: Infinity,
        y: room ? 0 : Infinity,
        z: 0,
      },
      max: {
        x: -Infinity,
        y: -Infinity,
        z: 0,
      },
    }
  );
  return out;
}

function getSize(tac, room, options) {
  const limits = getLimits(tac, room, options);

  return {
    width: limits.max.x - limits.min.x,
    height: limits.max.y - limits.min.y,
    depth: limits.max.z - limits.min.z,
  };
}

function getSpace(tac, wallResizerActive) {
  if (isFixedRoom()) {
    return wallResizerActive ? getMaxSpace() : getCurrentSpace(tac);
  }
  return getInitialSpace(tac);
}

function getInitialSpace(tac) {
  const limits = getLimits(tac);
  const width = limits.max.x - limits.min.x;
  const diff = constants.ROOM_MAX.width - width;

  return {
    width: width + 2 * diff,
    height: constants.ROOM_MAX.height,
    depth: constants.ROOM_DEPTH,
    x: limits.min.x - diff,
    y: 0,
    z: 0,
  };
}

function getCurrentSpace(tac) {
  if (tac && tac.wall) {
    const { width, height } = geometry.surround(tac.wall.points);

    return {
      x: 0,
      y: 0,
      z: 0,
      width,
      height,
      depth: constants.ROOM_DEPTH,
    };
  } else {
    const defaultWallPoints = [...constants.WALL.points];
    const xValues = defaultWallPoints.map(point => point.x);
    const yValues = defaultWallPoints.map(point => point.y);

    return {
      x: Math.min(...xValues),
      y: Math.min(...yValues),
      z: 0,
      width: Math.max(...xValues),
      height: Math.max(...yValues),
      depth: constants.ROOM_DEPTH,
    };
  }
}

function getMaxSpace() {
  return {
    x: 0,
    y: 0,
    z: 0,
    width: constants.WALL.width.max,
    height: constants.WALL.height.max,
    depth: constants.ROOM_DEPTH,
  };
}

function getSpaceForItem(space, item) {
  if (isWallMounted(item) && !isFloorStanding(item)) {
    // Items that only fit on the wall can't use the skirt area
    return {
      ...space,
      y: space.y + constants.SKIRT_HEIGHT,
      height: space.height - constants.SKIRT_HEIGHT,
    };
  }
  return space;
}

/**
 * Function to get room size with result of width/height
 *
 * @param {Object} tac an object of the tac should be passed to this function
 * @param {Number} ratio a number representing the ratio
 * @param {Object} minRoom a config with minimum width and height to calculate dimensions
 * @param {Object} options an object with options to be passed to the function
 * @returns {Object} an object with width and height dimensions.
 */
function getRoom(tac, ratio, minRoom, options = {}) {
  if (minRoom?.width == null || minRoom?.height == null) {
    //NOTE: This missing config attribute can cause the page to be broken so we double check on it here
    throw new Error(
      'Missing Configuration: minRoom is not defined in the constants config'
    );
  }

  function getWidth(height) {
    return floor(
      ratio * (height + bottomDepth) - leftDepth,
      constants.GRID.x.step
    );
  }

  function getHeight(width) {
    return floor(
      (width + leftDepth) / ratio - bottomDepth,
      constants.GRID.y.step
    );
  }

  const size = tac
    ? getSize(tac, true, options)
    : { width: 0, height: 0, depth: 0 };
  const leftDepth =
    Math.cos(constants.OBLIQUE_ANGLE) * constants.ROOM_DEPTH * 0.29;
  const bottomDepth =
    Math.sin(constants.OBLIQUE_ANGLE) * constants.ROOM_DEPTH * 0.29;

  const minimumSpaceRatio =
    (minRoom.width + leftDepth) / (minRoom.height + bottomDepth);

  let height;
  let width;

  if (size.width < minRoom.width && size.height < minRoom.height) {
    if (ratio > minimumSpaceRatio) {
      height = minRoom.height;
      width = getWidth(height);
    } else {
      width = minRoom.width;
      height = getHeight(width);
    }
  } else if (size.width < minRoom.width) {
    height = size.height;
    width = getWidth(height);

    if (width < minRoom.width) {
      width = minRoom.width;
      height = getHeight(width);
    }
  } else if (size.height < minRoom.height) {
    width = size.width;
    height = getHeight(width);

    if (height < minRoom.height) {
      height = minRoom.height;
      width = getWidth(height);
    }
  } else {
    height = size.height;
    width = getWidth(height);

    if (width < size.width) {
      width = size.width;
      height = getHeight(width);
    }
  }

  return {
    height,
    width,
  };
}

function withAllowedOverlaps(item, tac, fitItem) {
  if (!fitItem) {
    return item;
  }

  const allowedOverlap = range.getAllowedOverlap(tac, fitItem, item);

  if (!allowedOverlap) {
    return item;
  }

  const out = Object.assign({}, item);

  out.x += allowedOverlap.x;
  out.y += allowedOverlap.y;
  out.z += allowedOverlap.z;
  out.width += allowedOverlap.width;
  out.height += allowedOverlap.height;
  out.depth += allowedOverlap.depth;
  out.overlap = allowedOverlap;

  return out;
}

function withSpaceBounds(item, tac, fitItem) {
  let bounds = getBounds(item, 'space');
  if (fitItem && tac && item.logic && item.logic.extendable) {
    let shouldExpand;
    if (!fitItem.itemid) {
      shouldExpand = true;
    } else {
      const topAncestor = getTopAncestor(tac, item);
      const connectsToTopAncestor =
        topAncestor &&
        item.connectsTo &&
        getTopAncestor(tac, getItem(tac, item.connectsTo.itemid));
      shouldExpand =
        topAncestor &&
        topAncestor.itemid !== fitItem.itemid &&
        connectsToTopAncestor &&
        connectsToTopAncestor.itemid !== fitItem.itemid;
    }

    if (shouldExpand) {
      bounds = expandBounds(item, bounds);
    }
  }
  const out = Object.assign({}, item);
  out.x = item.x + bounds.pos.x;
  out.y = item.y + bounds.pos.y;
  out.z = item.z + bounds.pos.z;

  out.height = bounds.size.height;
  out.width = bounds.size.width;
  out.depth = bounds.size.depth;

  return out;
}

function getItemRects(item, omit, includeAll, parent = { x: 0, y: 0 }) {
  if (omit && item.itemid === omit.itemid) {
    return [];
  }
  const globalMe = Object.assign({}, item);
  globalMe.x = item.x + parent.x;
  globalMe.y = item.y + parent.y;

  const localMe = Object.assign({}, item);
  localMe.x = 0;
  localMe.y = 0;

  const kids = Array.isArray(item.items)
    ? flatten(item.items.map(item => getItemRects(item, omit, includeAll)))
    : [];
  return kids
    .filter(kid => includeAll || geometry.extendsOutside(kid, localMe))
    .map(localKid => {
      return {
        ...localKid,
        x: localKid.x + globalMe.x,
        y: localKid.y + globalMe.y,
      };
    })
    .concat(globalMe);
}

function getRawRects(tac, omit, includeAll) {
  return flatten(tac.items.map(item => getItemRects(item, omit, includeAll)));
}

function getRects(tac, omit, fullTac, includeAll = false) {
  return getRawRects(tac, omit, includeAll)
    .map(rect =>
      shouldIgnoreSpacingBounds(rect, omit, tac)
        ? rect
        : withSpaceBounds(rect, fullTac, omit)
    )
    .map(rect => withAllowedOverlaps(rect, fullTac, omit));
}

function hasPegboard(tac, filters = {}) {
  const allItems = getAllItems(tac.items);
  const someItems = filter(allItems, filters);
  return someItems.some(item => {
    return productService.isPegboard(item.id);
  });
}

function hasExtClothesRail(item) {
  const allItems = getAllItems(item.items || []);
  return allItems.some(
    item =>
      productService.isType(item, ITEMS.CLOTHES_RAIL) &&
      productService.isExtendable(item)
  );
}

function hasTable(item) {
  const allItems = getAllItems(item.items || []);
  return allItems.some(item => productService.isType(item, ITEMS.TABLE));
}

function hasBracket(item) {
  const allItems = getAllItems(item.items || []);
  return allItems.some(item => productService.isType(item, ITEMS.BRACKET));
}

function isClothesRailConnected(tac, item) {
  const topAncestor = getTopAncestor(tac, item);
  const itemAndChildren = getAllItems([topAncestor]);
  const allOtherItems = getAllItems(tac.items, topAncestor);

  return allOtherItems.some(
    item =>
      item.connectsTo &&
      itemAndChildren.some(child => child.itemid === item.connectsTo.itemid)
  );
}

function getConnectedClothesRails(tac, item) {
  const topAncestor = getTopAncestor(tac, item);
  const itemAndChildren = getAllItems([topAncestor]);
  const allOtherItems = getAllItems(tac.items, topAncestor);

  return (
    allOtherItems.filter(
      item =>
        item.connectsTo &&
        itemAndChildren.some(child => child.itemid === item.connectsTo.itemid)
    ) || []
  );
}

function hasSection(tac) {
  return tac.items.some(productService.isSection);
}

function modifySlot(slot, currentItem, rects, room, origo) {
  if (currentItem.items) {
    const newSlot = { ...slot };

    rects = rects.concat(
      {
        x: origo.x - 100,
        y: 0,
        height: room.height,
        width: 100,
      },
      {
        x: origo.x + room.width,
        y: 0,
        height: room.height,
        width: 100,
      }
    );

    const currentRects = getRects(currentItem);

    if (
      currentRects.every(item => {
        if (item.x < 0) {
          item = {
            ...item,
            x: newSlot.x + item.x,
            y: newSlot.y + item.y,
          };
          const colliding = geometry.getCollidingRects(item, rects);

          const offset = colliding.reduce(
            (offset, collider) =>
              Math.max(collider.x + collider.width - item.x, offset),
            0
          );

          if (newSlot.width - offset < currentItem.width) {
            return false;
          }

          newSlot.x += offset;
          newSlot.width -= offset;
        } else if (
          item.x >= currentItem.width ||
          item.x + item.width > currentItem.width
        ) {
          item = {
            ...item,
            x: newSlot.x + (newSlot.width - currentItem.width) + item.x,
            y: newSlot.y + item.y,
          };

          const colliding = geometry.getCollidingRects(item, rects);

          const offset = colliding.reduce(
            (offset, collider) =>
              Math.max(item.x + item.width - collider.x, offset),
            0
          );

          if (newSlot.width - offset < currentItem.width) {
            return false;
          }

          newSlot.width -= offset;
        }
        return true;
      })
    ) {
      return newSlot;
    } else {
      return null;
    }
  }

  return slot;
}

/**
 * Iterates over a changed items children, replacing them with versions that match their new parent.
 *
 * @param {Object} from The original parent
 * @param {Object} to The new version of the parent
 * @param {Object} filter Settings for how and what should be changed
 * @param {Object} filter Which filter properties the change should be based
 * @param {Object} [options] Additional options sometimes needed to conduct the change
 * @returns {Array} The replaced versions of the children
 */
function getNewChildren(from, to, filter, options = {}) {
  if (!Array.isArray(from.items)) {
    return [];
  }

  let parent;

  return from.items
    .map(item => {
      if (options.targetType && options.targetType !== item.filter.type) {
        return item;
      } else if (productService.isMultiParentProduct(item)) {
        // we can't find slots for this item this way, so keep it
        return item;
      }

      parent =
        parent ||
        Object.assign({}, to, {
          // BOAXEL default section height is 1mm
          height: to.height > 1 ? to.height : from.height,
          items: [...(to.items || [])],
          itemid: to.itemid || idGenerator.fakeId(),
        });

      const slotSources = options.slotSources || [];
      let slots = getOpenSlots({ items: [parent, ...slotSources] }, item);

      const grandParent = options.newParent;
      if (!slots.length && grandParent) {
        // No slots found, but we might find some if we mock the tac change one level higher
        const fakeGrandParent = Object.assign({}, grandParent);
        fakeGrandParent.items = [...grandParent.items, parent];
        const fakeTac = { items: [fakeGrandParent] };
        slots = getOpenSlots(fakeTac, item);
      }

      if (!slots.length) {
        // still no matches. remove.
        return null;
      }

      const localSlots = slots.map(slot => ({
        ...slot.local,
        width: slot.width,
        parent: slot.parent,
      }));

      const parentSlots = localSlots.filter(
        slot => slot.parent.itemid === parent.itemid
      );

      const collidingSlots = geometry.getCollidingRects(item, parentSlots);

      let slot =
        collidingSlots.length || parentSlots.length
          ? geometry.closestCollidingRect(
              item,
              collidingSlots.length ? collidingSlots : parentSlots
            )
          : localSlots[0];

      const targetSlot = {
        width: slot.width,
        depth: slot.depth,
        logic: parent.logic,
      };

      if (!targetSlot[filter.switchingProp]) {
        /*
        If we are switching on "variant", this property is never relevant on children of a different type than its parent,
        e.g when switching to a shelving unit of variant "narrow", we don't want to use this variant for legs/pads.
        Other variantTypes might be relevant for children however,
        e.g. changing "depth" on a section should be used for it's children as well.
        */
        targetSlot[filter.switchingProp] =
          filter.switchingProp !== 'variant' || filter.type === item.filter.type
            ? filter[filter.switchingProp]
            : null;
      }

      const refitted = productService.getFit(item, targetSlot);
      if (refitted && productService.isType(refitted, 'leg')) {
        //Legs are a special case that needs a new slot calculation after the re-fit
        const slots = getOpenSlots({ items: [parent] }, refitted);
        const localSlots = slots.map(slot => ({
          ...slot.local,
          width: slot.width,
          parent: slot.parent,
        }));
        const collidingSlots = geometry.getCollidingRects(item, localSlots);
        slot = geometry.closestCollidingRect(item, collidingSlots);
      }

      // If we're using slotSources we cannot trust the slot positioning
      // since it will be relative to another item
      const isParentSlot = slot.parent?.itemid === parent.itemid;
      const pos = isParentSlot && {
        x: slot.x,
        y: slot.y,
        z: slot.z,
      };

      const newItem = {
        ...item,
        ...refitted,
        ...pos,
        itemid: item.itemid,
      };

      // if a frame with baskets is being replaced, the frame isn't refitted,
      // so replace with itself
      newItem.items = getNewChildren(
        item,
        ServiceSettings?.applicationName?.toLowerCase() === 'boaxel'
          ? { ...newItem, x: 0, y: 0 }
          : newItem,
        filter,
        options
      );

      if (item.logic && item.logic.extendable) {
        const testItem = { ...newItem };
        testItem.width = parent.width;
        if (!geometry.extendsOutside(testItem, parent, 'x')) {
          newItem.width = parent.width;
        }
      }

      parent.items.push(newItem);
      return newItem;
    })
    .filter(Boolean);
}

function getSwitchableItem(from, to, options = {}) {
  const replacementItem = {
    ...productService.getProduct(to.id),
    x: from.x,
    y: from.y,
    z: from.z,
    itemid: from.itemid,
    propping: from.propping,
    uprights: from.uprights,
  };

  if (productService.isExtendable(to)) {
    replacementItem.width = to.width || from.width;
  }

  // switchingProp can be e.g. depth, variant or color
  const filter = {
    type: to.type,
    switchingProp: options.switchingProp,
    [options.switchingProp]: to.value,
  };

  const newItem = {
    ...replacementItem,
    items: getNewChildren(from, replacementItem, filter, options),
  };

  return newItem;
}

function diffFromTac(tac, moveItem) {
  const original = getItem(tac, moveItem.itemid);
  const gOriginal = getGlobalCoords(original, tac);
  return {
    x: moveItem.x - gOriginal.x,
    y: moveItem.y - gOriginal.y,
    z: moveItem.z - gOriginal.z,
  };
}

/* return a tac with all items specified removed */
function filterTac(tac, itemids = []) {
  const rest = Object.assign({}, tac);
  rest.items = (tac.items || [])
    .filter(item => !itemids.includes(item.itemid))
    .map(item => filterTac(item, itemids));
  return rest;
}

function isAncestor(tac, ancestor, descendant) {
  if (ancestor === descendant) {
    console.warn('checking if descendant is ancestor of itself!');
    return false;
  }
  if (ancestor && ancestor.itemid && descendant && descendant.itemid) {
    // ancestor may be stripped of some children ie the extendable clothes rail
    // so need to look up the real object
    ancestor = getItem(tac, ancestor.itemid);

    if (!ancestor) {
      console.warn('looking for a missing ancestor!');
      return false;
    }
    return getAllItems(ancestor.items || [])
      .map(item => item.itemid)
      .includes(descendant.itemid);
  }
  return false;
}

function isWithinWall(item, tac = null, isGlobal = false) {
  const gItem = tac && !isGlobal ? getGlobalCoords(item, tac) : item;

  if (gItem.y < 0) {
    return false;
  }

  if (!isFixedRoom() || !tac?.wall) {
    return true;
  }

  const wall = {
    ...geometry.surround(tac.wall.points),
    depth: constants.ROOM_DEPTH,
  };

  return geometry.contains(wall, gItem);
}

function getWallLimits(tac) {
  if (!tac.wall) {
    return;
  }

  return {
    max: {
      x: Math.max(...tac.wall.points.map(point => point.x)),
      y: Math.max(...tac.wall.points.map(point => point.y)),
    },
    min: {
      x: Math.min(...tac.wall.points.map(point => point.x)),
      y: Math.min(...tac.wall.points.map(point => point.y)),
    },
  };
}

function getVerticalNeighbours(section, superSection) {
  const sections = superSection.filter(
    item => item.itemid !== section.itemid && productService.isSection(item)
  );

  return sections.filter(cand => {
    return geometry.collides(
      { ...section, y: 0, height: constants.ROOM_MAX.height },
      cand
    );
  });
}

function extendableLimits(tac, item) {
  if (!productService.isAdjustable(item)) {
    return null;
  }
  if (productService.isType(item, ITEMS.CLOTHES_RAIL)) {
    return crLimits(tac, item);
  }
  if (productService.isType(item, ITEMS.SECTION)) {
    // if there are other sections that could interfere, disallow

    const supersection = findSuperSection(tac, item);
    const blockers = getVerticalNeighbours(item, supersection);

    if (blockers.length) {
      /*
      Vertical neighbours (inside same supersection) are considered blockers,
      since it can be very hard to pre-validate width change.

      but, we ignore lagkapten sections that are empty
      */

      const relevant = blockers.filter(
        blocker => blocker.filter.switchable || blocker.items?.length
      );

      if (relevant.length) {
        return null;
      }
    }

    const xMargin = getXmargin(tac);

    return {
      min: asConfig.section.minWidth,
      max: Math.min(asConfig.section.maxWidth, item.width + xMargin),
    };
  }
}
function getCondensedWidth(items) {
  return items.reduce(
    (result, item) => {
      if (item.x + item.width > result.reachedX) {
        // item can overlap backwards so need to adjust
        const backwardOverlap =
          item.x < result.reachedX ? result.reachedX - item.x : 0;
        result.width += item.width - backwardOverlap;
        result.reachedX = item.x + item.width;
      }
      return result;
    },
    { width: 0, reachedX: Math.min(...items.map(item => item.x)) }
  ).width;
}

function getCondensedTacWidth(tac) {
  const fullItems = tac.items.map(getFullSize).sort((a, b) => a.x - b.x);
  return getCondensedWidth(fullItems);
}

function getXmargin(tac) {
  const xAdjustedItems = convert.resetX([...tac.items]);
  const fullItems = xAdjustedItems.map(getFullSize).sort((a, b) => a.x - b.x);
  const condensedTacWidth = getCondensedWidth(fullItems);
  const roomWidth =
    tac.wall && tac.wall.points
      ? geometry.surround(tac.wall.points).width
      : constants.ROOM_MAX.width;
  return roomWidth - condensedTacWidth;
}

function crLimits(tac, cr) {
  const topAncestor = getTopAncestor(tac, cr);
  const chain = getClothesRailChain(tac, topAncestor, cr);
  if (
    [chain.leftEnd, chain.rightEnd].every(end =>
      productService.isType(end, 'sidewall')
    )
  ) {
    return {
      max: cr.width,
      min: cr.width,
    };
  }
  const crConfig = getItemConfig(cr);
  const mountOffset = crConfig.mountOffset;
  const limits = {
    max: crConfig.maxWidth,
    min: crConfig.minWidth,
  };

  const xMargin = getXmargin(tac);

  limits.max = Math.min(limits.max, cr.width + xMargin);

  //FIXME: Remove this?
  // if there's a wider clothes rail above us, this limits expansion
  const rects = getRawRects(tac);
  const crRect = rects.find(rect => rect.itemid === cr.itemid);
  if (!crRect) return;
  const otherRects = rects
    .filter(rect => rect !== crRect)
    .filter(
      rect =>
        !(
          productService.isMultiParentProduct(rect) &&
          rect.width === crRect.width
        )
    );

  // expand cr to reach floor and ceiling
  crRect.height = constants.ROOM_MAX.height;
  crRect.y = 0;
  // and tighten it a bit
  crRect.x += mountOffset + 1;
  crRect.width -= mountOffset * 2 + 2;

  const collisions = geometry.getCollidingRects(crRect, otherRects);

  if (collisions.length) {
    // if we collide with another clothes rail that's another width,
    // we don't allow resize.
    if (collisions.some(productService.isMultiParentProduct)) {
      return {
        min: cr.width,
        max: cr.width,
      };
    }
    const parents = collisions.filter(
      item => getTopAncestor(tac, item) === item
    );

    const fullParents = parents.map(getFullSize);
    const condensedParentsWidth = getCondensedWidth(fullParents);
    limits.min = Math.max(limits.min, condensedParentsWidth + 2 * mountOffset);
  }

  return limits;
}

function getFullSize(item, parent = null) {
  const out = {
    x: item.x,
    y: item.y,
    height: item.height,
    width:
      //The ext CR is allowed to deduct the part of itself that will be extending into another frame
      productService.isMultiParentProduct(item)
        ? item.width - (getItemConfig(item)?.mountOffset || 0)
        : item.width,
    logic: item.logic,
    filter: item.filter,
    valid: item.valid,
  };
  if (parent && item.x + item.width > parent.width) {
    out.verticalExtensionPoint = parent.y + item.y - item.height;
  }
  if (!item.items || !item.items.length) {
    return out;
  }

  const children = item.items.map(child => getFullSize(child, item));
  const merged = geometry.mergeKids(item, children);
  merged.logic = item.logic;
  merged.filter = item.filter;
  merged.valid = item.valid;
  merged.items = item.items;

  if (children.some(child => child.verticalExtensionPoint)) {
    merged.verticalExtensionPoint = children
      .filter(child => child.verticalExtensionPoint)
      .reduce(function (prev, current) {
        return prev.verticalExtensionPoint < current.verticalExtensionPoint
          ? prev
          : current;
      }).verticalExtensionPoint;
  }
  return merged;
}

function getClothesRailChain(tac, item, cr) {
  const config = getItemConfig(cr);
  function findLeftmostItem(item, otherItems, tac) {
    function findLeft(item, otherItems, tac) {
      const fullSizeItem = getFullSize(item);
      const leftTopAncestorsWithCrs = otherItems.filter(
        left =>
          left.x < fullSizeItem.x &&
          (range.moveWithCr(left) || isClothesRailConnected(tac, left))
      );

      const collidingItems = [];
      leftTopAncestorsWithCrs.forEach(item => {
        const fullSize = getFullSize(item);

        if (hasExtClothesRail(item)) {
          fullSize.width += config.mountOffset;
        }
        if (
          geometry.getCollidingRects(
            fullSizeItem,
            [fullSize],
            hasExtClothesRail(item) && { left: 1 }
          ).length
        ) {
          collidingItems.push(item);
        }
      });

      if (collidingItems.length === 0) {
        return [];
      }

      const closestItem = geometry.closest(collidingItems, 'left');
      const remaining = leftTopAncestorsWithCrs.filter(
        i => i.itemid !== closestItem.itemid
      );
      return [closestItem].concat(findLeft(closestItem, remaining, tac));
    }

    const leftItems = findLeft(item, otherItems, tac);

    return (
      leftItems.length &&
      leftItems.reduce(function (prev, current) {
        return prev.x < current.x ? prev : current;
      })
    );
  }

  function findRightmostItem(item, otherItems, tac) {
    function findRight(item, otherItems, tac) {
      const fullSizeItem = getFullSize(item);
      const rightTopAncestorsWithCrs = otherItems.filter(
        right =>
          right.x > fullSizeItem.x &&
          (range.moveWithCr(right) || isClothesRailConnected(tac, right))
      );

      fullSizeItem.width += config.mountOffset;
      const collidingItems = geometry.getCollidingRects(
        fullSizeItem,
        rightTopAncestorsWithCrs,
        hasExtClothesRail(item) && { right: 1 }
      );

      if (collidingItems.length === 0) {
        return [];
      }
      const closestItem = geometry.closest(collidingItems, 'right');
      const remaining = rightTopAncestorsWithCrs.filter(
        i => i.itemid !== closestItem.itemid
      );

      const nextItem = range.moveWithCr(closestItem) ? closestItem : item;

      return [closestItem].concat(findRight(nextItem, remaining, tac));
    }

    const rightItems = findRight(item, otherItems, tac);

    return (
      rightItems.length &&
      rightItems.reduce(function (prev, current) {
        return prev.x > current.x ? prev : current;
      })
    );
  }

  const parent = getParent(tac, item);

  const otherItems = parent.items
    ? parent.items.filter(i => i.itemid !== item.itemid)
    : [];

  const chain = {
    leftEnd: findLeftmostItem(item, otherItems, tac) || item,
    rightEnd: findRightmostItem(item, otherItems, tac),
  };

  return chain;
}

function withoutPartner(tac, partner) {
  function without(items, remove) {
    if (items.find(item => item.itemid === remove.itemid)) {
      return items.filter(item => item.itemid !== remove.itemid);
    }
    return items.map(item => ({
      ...item,
      items: without(item.items || [], remove),
    }));
  }

  const out = {
    ...tac,
    items: without(tac.items, partner),
  };
  return out;
}

function getOpenPartnerSlots(tac, partner, options) {
  return getPartnerSlots(tac, partner, options, getOpenSlots);
}

function getPartnerSlots(tac, partner, options = {}, slotFinder = getSlots) {
  const config = range.getPartnerConfig(partner);
  const outsideProduct = productService.getProduct(config.outerId);
  const partnerlessTac = withoutPartner(tac, partner);
  options.isPartnerCall = true;

  return slotFinder(partnerlessTac, outsideProduct, options);
}

function findSuperSection(tac, member, skiplist = []) {
  return range.findSuperSection(tac, member, skiplist);
}

function isSuperSectionHandle(item) {
  return range.isSuperSectionHandle(item);
}

function getSuperSectionSpace(tac, supersection) {
  return range.getSuperSectionSpace(tac, supersection);
}

function getProppingItemsToAdapt(tac, movingItem, previous, original) {
  return range.getProppingItemsToAdapt(tac, movingItem, previous);
}

/**
 * Gets the snapping position (x-position) to the closest colliding item.
 * @param {Object} item
 * @param {Array<Object>} rects
 * @returns The snapping x-position to the closest colliding item.
 */
function getSnappingPosition(item, rects) {
  return range.getSnappingPosition?.(item, rects);
}

/**
 * Gets the y-coordinate that the item should snap to if the item is placed on the skirt.
 * @param {Object} item
 * @returns {Object} An object with the y-coordinate that the item should snap to in relation to the skirt.
 */
function getSkirtSnappingPosition(item) {
  return {
    y: this.getSkirtSnappingYCoordinate(item),
  };
}

/**
 * If the y-coordinate of the item is less than half of the skirt height it will snap to the floor, if its more than half
 * of the skirt height it will snap to the top of the skirt.
 * @param {Object} item
 * @returns {number} The y-coordinate where the item shold snap to.
 */
function getSkirtSnappingYCoordinate(item) {
  const halfOfSkirtHeight = constants.SKIRT_HEIGHT / 2;
  return item.y >= halfOfSkirtHeight ? constants.SKIRT_HEIGHT : 0;
}

function getWallResizingLimits(tac) {
  return range.getWallResizingLimits?.(tac) || getLimits(tac);
}

function shouldIgnoreCollision(item, parent, candidate) {
  if (range.shouldIgnoreCollision?.(item, parent, candidate)) {
    return true;
  }

  return (
    (item.logic?.noCollision || candidate.logic?.noCollision) &&
    item.filter.type !== candidate.filter.type
  );
}

function isPartOf(item, parent) {
  return (
    parent.parts && Object.values(parent.parts).some(part => part === item.id)
  );
}

/**
 * Checks whether an incoming item is allowed to ignore the spacing bounds of
 * an already present item.
 *
 * @param {Object} candidate The item asking to ignore its spacing bounds
 * @param {Object} relative The other item in the relation
 * @param {Object} [tac] The entire TAC
 * @returns {Boolean | undefined} True/false depending on if ignoring is allowed,
 * or undefined if no range specific implementation exists
 */
function shouldIgnoreSpacingBounds(candidate, relative, tac) {
  return range.shouldIgnoreSpacingBounds?.(candidate, relative, tac);
}

function isItem(item) {
  return !!item.itemid;
}

/**
 * Recursive function for updating all items with certain ids
 * @param items {array}
 * @param ids {array}
 * @param updateFunc {function} Function for doing the updating. Need to return an object
 * @param args {any} Any other args that might be needed for the function doing the updating
 * @returns {*}
 */
const addToAllWithId = (items, ids, updateFunc, ...args) =>
  items.map(item => {
    const obj = {
      ...item,
      ...(item.items && {
        items: addToAllWithId(item.items, ids, updateFunc, ...args),
      }),
    };

    return ids.includes(item.id)
      ? { ...obj, ...updateFunc(item, ...args) }
      : obj;
  });

export default {
  clearCache,
  extendableLimits,
  diffFromTac,
  filterTac,
  getItem,
  getItemIds,
  getItems,
  getAllItems,
  getLimits,
  getTopAncestor,
  getPotentialBlockers,
  getParent,
  getGlobalCoords,
  getItemRects,
  getRawRects,
  getRects,
  getRoom,
  getRoomSlots,
  getOpenSlots,
  getSlots,
  getClothesRails,
  getSections,
  getSize,
  getSpace,
  getInitialSpace,
  getCurrentSpace,
  getMaxSpace,
  getSpaceForItem,
  hasOpenSlot,
  hasSection,
  hasExtClothesRail,
  hasTable,
  hasBracket,
  isClothesRailConnected,
  isAncestor,
  isWithinWall,
  getWallLimits,
  findSuperSection,
  isSuperSectionHandle,
  getSuperSectionSpace,
  getVerticalNeighbours,
  modifySlot,
  hasPegboard,
  getConnectedClothesRails,
  getSwitchableItem,
  getNewChildren,
  getFullSize,
  getClothesRailChain,
  getProppingItemsToAdapt,
  getXmargin,
  getSnappingPosition,
  getSkirtSnappingPosition,
  getSkirtSnappingYCoordinate,
  withAllowedOverlaps,
  isUnchanged,
  getCondensedTacWidth,
  getWallResizingLimits,
  shouldIgnoreCollision,
  isPartOf,
  shouldIgnoreSpacingBounds,
  isItem,
  addToAllWithId,
  itemsUnchanged,
  hasRealArticle,
  range,
  __test__: {
    getCondensedWidth,
  },
};
